import React, { useState } from 'react';
import { Download, ImageOff } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';

const stamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
};

const downloadHref = (photo) => photo?.download_url || photo?.url;

/**
 * Read-only proof gallery: labelled photo groups with who/when, click to
 * enlarge, and a download on every photo (thumbnail and enlarged view).
 *
 * Shared by the staff delivery stop card, the admin Delivery Proof panel and
 * the client's Proof of Delivery card — the client payload simply carries no
 * uploader name, which the row below each thumbnail handles.
 */
export const PhotoProofGallery = ({ groups = [], testIdPrefix = 'proof', emptyLabel = 'No photos on this order' }) => {
  const [active, setActive] = useState(null);
  const visible = groups.filter((g) => (g.photos || []).length > 0);

  if (visible.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 py-4" data-testid={`${testIdPrefix}-empty`}>
        <ImageOff className="h-4 w-4" />
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-gallery`}>
      {visible.map((group) => (
        <div key={group.key} data-testid={`${testIdPrefix}-group-${group.key}`}>
          <div className="flex items-baseline gap-2 flex-wrap mb-2">
            <p className="text-sm font-medium text-gray-800">{group.title}</p>
            <span className="text-xs text-gray-400">
              {group.photos.length} photo{group.photos.length === 1 ? '' : 's'}
              {group.caption ? ` · ${group.caption}` : ''}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
            {group.photos.map((photo, index) => (
              <div key={photo.url || index} className="space-y-1">
                <div className="relative aspect-square rounded-lg overflow-hidden border bg-gray-50 group">
                  <button
                    type="button"
                    onClick={() => setActive({ ...photo, groupTitle: group.title })}
                    className="w-full h-full"
                    data-testid={`${testIdPrefix}-thumb-${group.key}-${index}`}
                    aria-label={`Enlarge ${group.title} photo ${index + 1}`}
                  >
                    <img
                      src={photo.url}
                      alt={`${group.title} ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                    />
                  </button>
                  <a
                    href={downloadHref(photo)}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="absolute bottom-1 right-1 w-8 h-8 flex items-center justify-center rounded-full bg-black/60 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-black/80"
                    data-testid={`${testIdPrefix}-download-${group.key}-${index}`}
                    aria-label={`Download ${group.title} photo ${index + 1}`}
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>
                <p className="text-[11px] leading-tight text-gray-400 truncate" title={photo.uploaded_by_name || ''}>
                  {photo.uploaded_by_name ? `${photo.uploaded_by_name} · ` : ''}{stamp(photo.uploaded_at)}
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent className="max-w-3xl p-3" data-testid={`${testIdPrefix}-lightbox`}>
          <DialogHeader>
            <DialogTitle className="text-base">{active?.groupTitle || 'Photo'}</DialogTitle>
            <DialogDescription className="text-xs">
              {active?.uploaded_by_name ? `${active.uploaded_by_name} · ` : ''}{stamp(active?.uploaded_at)}
            </DialogDescription>
          </DialogHeader>
          {active && (
            <>
              <img
                src={active.url}
                alt={active.groupTitle || 'Proof photo'}
                className="w-full max-h-[70vh] object-contain rounded"
                data-testid={`${testIdPrefix}-lightbox-image`}
              />
              <a
                href={downloadHref(active)}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium self-center"
                data-testid={`${testIdPrefix}-lightbox-download`}
              >
                <Download className="h-4 w-4" />
                Download photo
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotoProofGallery;
